import { useEffect, useMemo } from 'react'

import { Route, RouteProps, useHistory, useLocation } from 'react-router-dom'

import { canSkipGuard, canSkipBackGuard } from '../../config/routes'
import { useAnswers } from '../../providers/AnswersProvider'
import { Answers } from '../../providers/AnswersProvider'
import { usePatient } from '../../providers/PatientProvider'
import { PatientProviderContextType } from '../../providers/PatientProvider'
import { useVisitedSteps } from '../../providers/VisitedStepsProvider'

type Props = RouteProps & {
  guard: (
    answers: Partial<Answers> | null,
    patient: PatientProviderContextType['patient'] | null,
    visitedSteps: string[] | null
  ) => { isValid: boolean; redirectTo?: string; path?: string }
  backGuard?: (
    answers: Partial<Answers> | null,
    patient: PatientProviderContextType['patient'] | null,
    visitedSteps: string[] | null
  ) => { isValid: boolean; redirectTo?: string; path?: string }
}

const GuardedRoute: React.FC<Props> = ({ guard, backGuard, ...props }) => {
  const history = useHistory()
  const location = useLocation()
  const answers = useAnswers()
  const patient = usePatient()
  const visitedSteps = useVisitedSteps()

  const { isValid, redirectTo, path } = useMemo(
    () => guard(answers, patient, visitedSteps),
    [guard, answers, patient, visitedSteps]
  )

  useEffect(() => {
    if (canSkipGuard) return

    const backGuardResult = backGuard?.(answers, patient, visitedSteps)

    if (backGuardResult?.isValid === false && backGuardResult.redirectTo) {
      if (canSkipBackGuard) return

      return history.push(backGuardResult.redirectTo)
    }

    if (!isValid && redirectTo) return history.push(redirectTo)
  }, [isValid, redirectTo, history, location, path, backGuard, answers, patient, visitedSteps])

  if (!isValid && location.pathname !== path && !canSkipGuard) return null

  return <Route {...props} />
}

export default GuardedRoute
